import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  APIProvider,
  Map,
  Marker,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';

const MapContent = ({ markerPosition }) => {
  const map = useMap();

  useEffect(() => {
    if (map && markerPosition) {
      map.panTo(markerPosition);
    }
  }, [map, markerPosition]);

  return markerPosition ? <Marker position={markerPosition} /> : null;
};

const AutocompleteInput = ({ setMarkerPosition, onLocationChange }) => {
  const inputRef = useRef(null);
  const map = useMap();
  useMapsLibrary('places'); // load 'places' library

  useEffect(() => {
    if (!map || !inputRef.current || !window.google?.maps?.places) return;

    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current);
    autocomplete.bindTo('bounds', map);

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const location = place?.geometry?.location;
      if (!location) return;

      const lat = location.lat();
      const lng = location.lng();
      setMarkerPosition({ lat, lng });
      onLocationChange(`${lat},${lng}`);
      map.panTo({ lat, lng });
      map.setZoom(15);
    });
  }, [map, setMarkerPosition, onLocationChange]);

  return (
    <input
      ref={inputRef}
      type="text"
      placeholder="Search places..."
      style={{
        width: '100%',
        height: '40px',
        zIndex: 100,
        border: '1px solid #ccc',
        borderRadius: '5px',
        backgroundColor: '#fff',
        padding: '0 12px',
        marginBottom: '20px',
      }}
    />
  );
};

const GoogleMapComponent = ({ isVisible, initialLocation, onLocationChange }) => {
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [markerPosition, setMarkerPosition] = useState(initialLocation);

  const onMapClick = useCallback((event) => {
    const latLng = event.detail?.latLng;
    if (!latLng || typeof latLng.lat !== 'number' || typeof latLng.lng !== 'number') return;

    const lat = latLng.lat;
    const lng = latLng.lng;
    setMarkerPosition({ lat, lng });
    onLocationChange(`${lat},${lng}`);
  }, [onLocationChange]);

  if (!googleMapsApiKey) {
    return <div className="text-red-600 font-medium">Error: API key missing.</div>;
  }

  return (
    <div className={`mt-2 h-56 ${isVisible ? 'relative' : 'hidden'}`}>
      <APIProvider apiKey={googleMapsApiKey} libraries={['places']} version="weekly">
        <AutocompleteInput
          setMarkerPosition={setMarkerPosition}
          onLocationChange={onLocationChange}
        />
        <Map
          style={{ height: '100%', width: '100%', borderRadius: '10px' }}
          defaultCenter={initialLocation}
          defaultZoom={13}
          gestureHandling="greedy"
          disableDefaultUI={true}
          onClick={onMapClick}
        >
          <MapContent markerPosition={markerPosition} />
        </Map>
        <p className="text-sm text-gray-600 font-medium mt-1">
          Location: {markerPosition?.lat?.toFixed(3)}, {markerPosition?.lng?.toFixed(3)}
        </p>
      </APIProvider>
    </div>
  );
};

export default GoogleMapComponent;